import React, { useEffect, useState } from 'react';
import { Box, CardContent, Typography, Button, Snackbar, Alert, Autocomplete, TextField, Chip } from '@mui/material';
import { AccountCircle, LocationCity } from '@mui/icons-material';
import { PageContainer, PageCard, SectionHeader } from '../common/components/styled-ui-elements';
import { ProfileData, GraphData } from '../common/components/styled-ui-elements/ProfileData';
import { BackToDashboardButton } from '../common/components/navigation';
import { useGraphProfile } from '../tickets/hooks';
import { useGetCentersQuery } from '../../services/DimensionService/dimensionApi';
import { useGetMyPreferencesQuery, useSaveMyPreferencesMutation } from '../../services/UserPreferencesService';
import { EnumView } from '../../services/DimensionService/types/dimensionTypes';

// Extracted so ProfilePage's own render logic doesn't carry this section's branching.
const ProfileInfoSection: React.FC<{ graphData: GraphData | null; graphError: Error | null }> = ({ graphData, graphError }) => {
  if (graphError) return <Typography color="error">Error loading profile information.</Typography>;
  if (!graphData) return <Typography sx={{ color: '#64748b' }}>Loading profile information...</Typography>;
  return <ProfileData graphData={graphData} />;
};

// Kept local to this page rather than shared — the near-identical control on AssetList is its
// own local copy too, per the "don't extract until a third consumer exists" rule.
const CentersFilter: React.FC<{ centers: EnumView[]; selectedCenters: string[]; onChange: (centers: string[]) => void }> = ({
  centers, selectedCenters, onChange,
}) => {
  const nameFor = (value: string) => centers.find((c) => c.value === value)?.name || value;

  return (
    <Autocomplete
      multiple
      size="small"
      options={centers.map((c) => c.value)}
      value={selectedCenters}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={nameFor}
      renderInput={(params) => (
        <TextField {...params} label="Default Centers" placeholder="Select Centers" />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={nameFor(option)} size="small" />
        ))
      }
    />
  );
};

// Loading / error / editable states for the Default Centers section, kept out of ProfilePage's
// own render logic so a failed load can neither be edited nor saved over the existing preference.
const DefaultCentersEditor: React.FC<{
  centers: EnumView[];
  isLoading: boolean;
  isError: boolean;
  selectedCenters: string[];
  onChange: (centers: string[]) => void;
}> = ({ centers, isLoading, isError, selectedCenters, onChange }) => {
  if (isLoading) return <Typography sx={{ color: '#64748b' }}>Loading default Centers...</Typography>;
  if (isError) return <Typography color="error">Failed to load default Centers. Try again later.</Typography>;
  return <CentersFilter centers={centers} selectedCenters={selectedCenters} onChange={onChange} />;
};

const saveResultSeverity = (result: 'success' | 'error') => (result === 'success' ? 'success' : 'error');
const saveResultMessage = (result: 'success' | 'error') =>
  result === 'success' ? 'Default Centers saved.' : 'Failed to save default Centers.';

const ProfilePage: React.FC = () => {
  const { graphData, error: graphError } = useGraphProfile();
  const { data: centers = [] } = useGetCentersQuery();
  const { data: preferences, isLoading: preferencesLoading, isError: preferencesError } = useGetMyPreferencesQuery();
  const [saveMyPreferences, { isLoading: isSaving }] = useSaveMyPreferencesMutation();

  const [selectedCenters, setSelectedCenters] = useState<string[]>([]);
  const [hasSeededDefaults, setHasSeededDefaults] = useState(false);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);

  // Seed the selection from the saved preference exactly once, so a later refetch (e.g. after
  // saving) doesn't clobber further edits the user makes on this page.
  useEffect(() => {
    if (!hasSeededDefaults && preferences) {
      setSelectedCenters(preferences.defaultCenters);
      setHasSeededDefaults(true);
    }
  }, [hasSeededDefaults, preferences]);

  // Also treat a manual edit as "seeded" — otherwise a preferences load that resolves after the
  // user has already changed the selection would overwrite their choice with the saved defaults.
  const handleCentersChange = (next: string[]) => {
    setSelectedCenters(next);
    setHasSeededDefaults(true);
  };

  const handleSave = async () => {
    try {
      await saveMyPreferences({ defaultCenters: selectedCenters }).unwrap();
      setSaveResult('success');
    } catch {
      setSaveResult('error');
    }
  };

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <Box sx={{ backgroundColor: '#ffffff', borderBottom: '2px solid #3b82f6', p: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <SectionHeader icon={<AccountCircle sx={{ mr: 1, color: '#3b82f6' }} />} title="View Profile" />
              <BackToDashboardButton />
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <ProfileInfoSection graphData={graphData} graphError={graphError} />

            <Box sx={{ mt: 3 }}>
              <SectionHeader icon={<LocationCity sx={{ mr: 1, color: '#d97706' }} />} title="Default Centers" />
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Choose the Centers you tend to. These will be selected by default on the Asset
                Management page.
              </Typography>

              <DefaultCentersEditor
                centers={centers}
                isLoading={preferencesLoading}
                isError={preferencesError}
                selectedCenters={selectedCenters}
                onChange={handleCentersChange}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={isSaving || preferencesLoading || preferencesError}
                  onClick={handleSave}
                  sx={{ backgroundColor: '#162346', '&:hover': { backgroundColor: '#1a2a52' }, fontWeight: 600, textTransform: 'none' }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </CardContent>
        </PageCard>
      </Box>

      <Snackbar open={saveResult !== null} autoHideDuration={4000} onClose={() => setSaveResult(null)}>
        <Alert severity={saveResultSeverity(saveResult ?? 'success')} onClose={() => setSaveResult(null)}>
          {saveResultMessage(saveResult ?? 'success')}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default ProfilePage;
